import { vm } from "../socket/config.js";
import { nameToTarget } from "../utils/scratch-helpers.js";
import { isLocalMode } from "../vm/local-mode.js";

const LOCK_TIMEOUT_MS = 5000;

const lockedTargets = new Map(); // targetName -> timeoutId
const overlays = new Map(); // targetName -> overlay element

const toPageCoords = (x, y, rect, nativeSize) => [
    rect.left + (rect.width / nativeSize[0]) * x + rect.width / 2,
    rect.top - (rect.height / nativeSize[1]) * y + rect.height / 2,
];

const updateOverlayPosition = (el, target) => {
    if (!vm.renderer || target.drawableID === undefined) return;

    const bounds = vm.renderer.getBounds(target.drawableID);
    const rect = vm.renderer.canvas.getBoundingClientRect();
    const nativeSize = vm.renderer.getNativeSize();

    const [left, top] = toPageCoords(bounds.left, bounds.top, rect, nativeSize);
    const [right, bottom] = toPageCoords(
        bounds.right,
        bounds.bottom,
        rect,
        nativeSize,
    );
    const pad = 4;

    el.style.left = `${Math.min(left, right) - pad}px`;
    el.style.top = `${Math.min(top, bottom) - pad}px`;
    el.style.width = `${Math.abs(right - left) + pad * 2}px`;
    el.style.height = `${Math.abs(bottom - top) + pad * 2}px`;
};

const createOverlay = (username, color) => {
    const el = document.createElement("div");
    el.style.cssText = `
        position: fixed;
        pointer-events: none;
        z-index: 40;
        border: 3px solid ${color};
        border-radius: 6px;
        box-sizing: border-box;
    `;

    const tag = document.createElement("div");
    const shownName =
        username?.length > 12 ? `${username.slice(0, 12)}...` : username;
    tag.textContent = shownName || "";
    tag.style.cssText = `
        position: absolute;
        top: -22px;
        left: -3px;
        background: ${color};
        color: #fff;
        font-size: 0.7rem;
        padding: 2px 6px;
        border-radius: 4px;
        white-space: nowrap;
    `;

    el.appendChild(tag);
    document.body.appendChild(el);
    return el;
};

const clearLock = (targetName) => {
    const timeoutId = lockedTargets.get(targetName);
    if (timeoutId) clearTimeout(timeoutId);
    lockedTargets.delete(targetName);

    const overlay = overlays.get(targetName);
    if (overlay) {
        overlay.remove();
        overlays.delete(targetName);
    }
};

const armLockTimeout = (targetName) => {
    const previous = lockedTargets.get(targetName);
    if (previous) clearTimeout(previous);
    lockedTargets.set(
        targetName,
        setTimeout(() => clearLock(targetName), LOCK_TIMEOUT_MS),
    );
};

export const isTargetLocked = (targetName) => lockedTargets.has(targetName);

export const onSpriteDragStart = (data) => {
    if (isLocalMode()) return;
    const targetName = data.data.target;

    clearLock(targetName);
    armLockTimeout(targetName);
    overlays.set(targetName, createOverlay(data.username, data.color));

    const target = nameToTarget(targetName);
    const overlay = overlays.get(targetName);
    if (target && overlay) updateOverlayPosition(overlay, target);
};

export const onSpriteDragPosition = (data) => {
    if (isLocalMode()) return;
    const { target: targetName, x, y } = data.data;
    const target = nameToTarget(targetName);
    if (!target) return;

    if (!lockedTargets.has(targetName)) {
        overlays.set(targetName, createOverlay(data.username, data.color));
    }
    armLockTimeout(targetName);

    target.x = x;
    target.y = y;
    if (target.renderer) target.updateAllDrawableProperties();

    const overlay = overlays.get(targetName);
    if (overlay) updateOverlayPosition(overlay, target);
};

export const onSpriteDragEnd = (data) => {
    clearLock(data.data.target);
};
